import React from "react"
import PropTypes from "prop-types"
import { Button, Flex, Form, Input } from "antd"
import { spacings } from "themes/variables"
import { validateStrongPassword } from "ui/auth/utils"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"

function getViewState(isTokenInvalid, isSubmitted) {
  if (isTokenInvalid) return "invalid"
  return isSubmitted ? "success" : "form"
}

const ConfirmPasswordContent = ({ isTokenVerified, isSubmitted, onSignInClick, onSubmit, onSubmitClick, form, isGuest }) => {
  const { t } = useTranslation()

  const view = getViewState(!isTokenVerified, isSubmitted)

  const headingText = {
    invalid: t("confirm_password.token_invalid"),
    success: t("confirm_password.success_message"),
    form: isGuest ? t("confirm_password.info_guest_message") : t("confirm_password.info_booking_message")
  }[view]

  return (
    <Flex vertical align="center" gap={spacings.L}>
      <Text color="primary" className="text-center">
        {headingText}
      </Text>

      {view === "success" && (
        <Button type="primary" onClick={onSignInClick}>
          {t("confirm_password.sign_in_cta")}
        </Button>
      )}

      {view === "form" && (
        <Form form={form} name="password-form" layout="vertical" onFinish={onSubmit} className="w-full">
          <Flex vertical gap={spacings.M}>
            <Form.Item
              name="password"
              label={t("confirm_password.labels.password")}
              rules={[{ required: true, message: "" }, { validator: (_, value) => validateStrongPassword(value, t) }]}
              hasFeedback
            >
              <Input.Password placeholder={t("confirm_password.placeholders.password")} disabled={!isTokenVerified} />
            </Form.Item>

            <Form.Item
              name="confirm"
              label={t("confirm_password.labels.confirm_password")}
              dependencies={["password"]}
              hasFeedback
              rules={[
                { required: true, message: t("confirm_password.validation.confirm_required") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error(t("confirm_password.validation.confirm_mismatch")))
                  }
                })
              ]}
            >
              <Input.Password placeholder={t("confirm_password.placeholders.confirm_password")} disabled={!isTokenVerified} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" onClick={onSubmitClick} disabled={!isTokenVerified}>
                {t("global.submit")}
              </Button>
            </Form.Item>
          </Flex>
        </Form>
      )}
    </Flex>
  )
}

ConfirmPasswordContent.propTypes = {
  isTokenVerified: PropTypes.bool.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  onSignInClick: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSubmitClick: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  isGuest: PropTypes.bool
}

ConfirmPasswordContent.defaultProps = {
  isGuest: false
}

export default ConfirmPasswordContent
